import React, { Component, Fragment } from "react";
import type { ReactNode } from "react";
import { Form, FormSpy } from "react-final-form";
import type { FormRenderProps } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import arrayMutators from "final-form-arrays";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { TextField } from "./TextField";
import { JsonField } from "./JsonField";
import { FieldComponent } from "./FieldComponent";
import { AutocompleteField } from "./AutocompleteField";
import { RadioGroup } from "./RadioGroup";
import { Checkbox } from "./Checkbox";
import { CheckboxGroupField } from "./CheckboxGroupField";
import { Chips } from "./Chips";
import { TextArrayField } from "./TextArrayField";
import { Switch } from "./Switch";
import { SwitchGroup } from "./SwitchGroup";
import { ColorPickerField } from "./ColorPickerField";
import { ImageField } from "./ImageField";
import { BannerField } from "./BannerField";
import { FileField } from "./FileField";
import { AddressField } from "./AddressField";
import { CompanyTitleField } from "./CompanyTitleField";
import { RangeInputField } from "./RangeInputField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "./DatePicker";
import { DateTimePicker } from "./DatetimePicker";
import { DateMonthPicker } from "./DateMonthPicker";
import { TimePicker } from "./TimePicker";
import { Markdown } from "./Markdown";
import { ReadOnlyFormField } from "./ReadOnlyFormField";
import { FORM_ERROR } from "final-form";
import { ErrorMessage } from "@/shared/components";

import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { tShared } from "@/shared/i18n";

import { Block } from "./Block";
import { ArrayField } from "./ArrayField";
import { PlatformBlocks } from "./PlatformBlocks";
import { SelectObjectField } from "./SelectObjectField";
import { CustomFields } from "./CustomFields";
import { CustomFieldsSchemaField } from "./CustomFieldsSchemaField";

import type {
  FieldDefinition,
  ArrayFieldDefinition,
  Schema,
  ConfirmData,
  BlockFieldDefinition,
  CardFieldDefinition,
  ColumnFieldDefinition,
  PlatformBlocksFieldDefinition,
  CustomFieldsFieldDefinition,
  CustomFieldsSchemaFieldDefinition,
} from "./types";
import { isUnloadPromptSuppressed } from "@/shared/utils/reloadWithoutPrompt";

interface FormBuilderProps {
  onSubmitSuccess?: (result: any) => void;
  onSubmitFailure?: (error: any) => void;
  onSubmit?: (values: Record<string, any>) => Promise<any> | any;
  editData?: Record<string, any>;
  initialValues?: Record<string, any>;
  objectId?: string;
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  subscription?: Record<string, boolean>;
  schema: Schema;
  disabled?: boolean;
  submitButton?: boolean;
  resetButton?: boolean;
  submitButtonText?: string;
  resetButtonText?: string;
  /** stretch — кнопки на всю ширину (по умолчанию), end — компактные справа */
  actionsAlign?: "stretch" | "end";
  header?: (params: { disabled: boolean }) => ReactNode;
  indent?: boolean;
  getConfirm?: (params: { values: Record<string, any> }) => ConfirmData | null;
  onChange?: (params: { values: Record<string, any> }) => void;
  // Новые пропсы для предупреждений о несохраненных изменениях
  warnOnUnsavedChanges?: boolean;
  unsavedChangesWarningMessage?: string;
  /**
   * Пробрасывается в react-final-form <Form>. При true поля, которые
   * пользователь уже изменил (dirty), не сбрасываются при пересчёте
   * initialValues (например, при live-обновлении данных по подписке/поллингу).
   * По умолчанию не задан (поведение react-final-form по умолчанию — false),
   * чтобы не менять поведение существующих форм.
   */
  keepDirtyOnReinitialize?: boolean;
  // Метаданные для передачи в поля без включения в данные формы
  metadata?: Record<string, any>;
  formId?: string;
  validate?: (values: unknown) => Record<string, any>;
  /** Обернуть форму в Paper */
  paper?: boolean;
}

interface FormBuilderState {
  activeTab: number;
  confirm?: ConfirmData | null;
  loadingConfirm?: boolean;
}

export class FormBuilder extends Component<FormBuilderProps, FormBuilderState> {
  private prevValues?: Record<string, any>;
  static defaultProps: Partial<FormBuilderProps> = {
    spacing: 2,
    subscription: {
      submitting: true,
      pristine: true,
    },
    onSubmit: (values: Record<string, any>) => Promise.resolve(values),
    onSubmitSuccess: () => {
      // Default empty implementation
    },
    onSubmitFailure: () => {
      // Default empty implementation
    },
    submitButton: true,
    resetButton: true,
    // submitButtonText / resetButtonText / unsavedChangesWarningMessage не
    // задаются здесь: defaultProps вычисляются на этапе загрузки модуля, когда
    // i18next ещё не инициализирован. Подписи резолвятся лениво — см. render()
    // и unsavedChangesMessage().
    actionsAlign: "stretch",
    warnOnUnsavedChanges: true, // ✅ Включено по умолчанию
    paper: false,
  };

  state: FormBuilderState = {
    activeTab: 0,
  };

  componentDidMount() {
    // Добавляем обработчики предупреждений при монтировании компонента
    if (this.props.warnOnUnsavedChanges) {
      this.addUnsavedChangesListeners();
    }
  }

  componentDidUpdate(prevProps: FormBuilderProps) {
    // Если изменился флаг warnOnUnsavedChanges, добавляем или убираем обработчики
    if (prevProps.warnOnUnsavedChanges !== this.props.warnOnUnsavedChanges) {
      if (this.props.warnOnUnsavedChanges) {
        this.addUnsavedChangesListeners();
      } else {
        this.removeUnsavedChangesListeners();
      }
    }
  }

  componentWillUnmount() {
    // Убираем обработчики при размонтировании компонента
    if (this.props.warnOnUnsavedChanges) {
      this.removeUnsavedChangesListeners();
    }
  }

  addUnsavedChangesListeners = () => {
    // Предупреждение о несохраненных данных при попытке покинуть страницу
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    // Блокировка навигации внутри приложения
    document.addEventListener("click", this.handleNavigationClick, true);
  };

  removeUnsavedChangesListeners = () => {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    document.removeEventListener("click", this.handleNavigationClick, true);
  };

  // Добавляем ссылку на текущее состояние pristine из React Final Form
  private currentPristine = true;

  private unsavedChangesMessage = (): string =>
    this.props.unsavedChangesWarningMessage ||
    tShared(
      "shared.form.unsavedChangesWarning",
      "У вас есть несохраненные изменения. Покинуть страницу без сохранения?"
    );

  handleBeforeUnload = (event: BeforeUnloadEvent) => {
    // Перезагрузку после успешного сохранения запустило само приложение.
    if (isUnloadPromptSuppressed()) return;
    // Используем то же состояние pristine, что и кнопка "Сохранить"
    const hasUnsavedChanges = !this.currentPristine;
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = this.unsavedChangesMessage();
      return event.returnValue;
    }
  };

  handleNavigationClick = (event: Event) => {
    // Проверяем, если клик по ссылке и есть несохраненные изменения
    const target = event.target as HTMLElement;
    const link = target.closest("a[href]") as HTMLAnchorElement;

    // Используем то же состояние pristine, что и кнопка "Сохранить"
    const hasUnsavedChanges = !this.currentPristine;
    if (link && hasUnsavedChanges) {
      const href = link.getAttribute("href");
      // Проверяем, что это внутренняя ссылка
      if (href && (href.startsWith("/") || href.startsWith("#"))) {
        if (!window.confirm(this.unsavedChangesMessage())) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  };

  // checkForUnsavedChanges удален - теперь используем pristine из React Final Form

  getFieldComponent = (type: string): React.ComponentType<any> => {
    switch (type) {
      case "radioGroup":
        return RadioGroup;
      case "checkbox":
        return Checkbox;
      case "checkboxGroup":
        return CheckboxGroupField;
      case "switch":
        return Switch;
      case "switchGroup":
        return SwitchGroup;
      case "color":
        return ColorPickerField;
      case "select":
        return AutocompleteField;
      case "autocomplete":
        return AutocompleteField;
      case "datetime":
        return DateTimePicker;
      case "date":
        return DatePicker;
      case "datemonth":
        return DateMonthPicker;
      case "time":
        return TimePicker;
      case "markdown":
        return Markdown;
      case "chips":
        return Chips;
      case "textArray":
        return TextArrayField;
      case "fieldComponent":
        return FieldComponent;
      case "json":
        return JsonField;
      case "image":
        return ImageField;
      case "banner":
        return BannerField;
      case "file":
        return FileField;
      case "address":
        return AddressField;
      case "rangeInput":
        return RangeInputField;
      case "readonly":
        return ReadOnlyFormField;
      case "selectObject":
        return SelectObjectField;
      case "text":
      case "password":
      case "email":
      case "url":
      case "number":
      default:
        return TextField;
    }
  };

  getField = (
    fieldName: string,
    field: FieldDefinition,
    values: Record<string, unknown>,
    allValues: Record<string, unknown>
  ): ReactNode => {
    const {
      label,
      type,
      lg,
      md,
      sm,
      xs,
      xl,
      visibleCheck,
      disabledCheck,
      ...restProps
    } = field;

    if (visibleCheck && !visibleCheck({ values, allValues })) {
      return null;
    }

    const disabled = disabledCheck && disabledCheck({ values, allValues });

    // Helper function to resolve label
    const resolvedLabel =
      typeof label === "function" ? label({ values, allValues }) : label;

    // Helper function to resolve helperText (используется полями, которые
    // возвращаются раньше общей обработки processedProps в конце метода)
    const resolvedHelperText =
      typeof field.helperText === "function"
        ? field.helperText({ value: values[fieldName], allValues })
        : field.helperText;

    // Handle title field
    if (type === "title") {
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <Typography variant={field.variant || "h6"}>
            {resolvedLabel}
          </Typography>
          {field.helperText && (
            <Typography variant="body2" color="text.secondary">
              {typeof field.helperText === "function"
                ? field.helperText({ allValues })
                : field.helperText}
            </Typography>
          )}
        </Grid>
      );
    }

    // Handle companyTitle field
    if (type === "companyTitle") {
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <CompanyTitleField
            {...restProps}
            label={resolvedLabel}
            editData={this.props.editData}
            allValues={allValues}
          />
        </Grid>
      );
    }

    // Handle readonly field - не является частью формы
    if (type === "readonly") {
      const FieldComponent = this.getFieldComponent(type);
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs || 12,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <FieldComponent
            {...restProps}
            label={resolvedLabel}
            editData={this.props.editData}
            allValues={allValues}
            disabled={disabled}
            fieldPath={(restProps as any).fieldPath}
          />
        </Grid>
      );
    }

    // Handle block field
    if (type === "block") {
      const blockField = field as BlockFieldDefinition;

      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <Block
            title={resolvedLabel || fieldName}
            hideHeader={blockField.hideHeader}
            alwaysOpen={blockField.alwaysOpen}
          >
            <Grid container spacing={this.props.spacing || 2}>
              {Object.keys(blockField.subschema || {}).map((subName) => {
                const subField = blockField.subschema?.[subName];
                if (!subField) return null;

                // Для блоков не добавляем имя блока в путь к полю - блок только для группировки UI
                // Используем родительский путь напрямую
                const parentPath = fieldName.split(".").slice(0, -1).join(".");
                const fullFieldName = parentPath
                  ? `${parentPath}.${subName}`
                  : subName;

                return (
                  <React.Fragment key={subName}>
                    {this.getField(fullFieldName, subField, values, allValues)}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Block>
        </Grid>
      );
    }

    // Handle column field — layout-only wrapper, optional label; not a form field
    if (type === "column") {
      const columnField = field as ColumnFieldDefinition;

      return (
        <Grid key={fieldName} size={{ xs, sm, md, lg, xl }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {resolvedLabel && (
              <Typography variant="h6">{resolvedLabel}</Typography>
            )}
            <Grid container spacing={this.props.spacing || 2}>
              {Object.keys(columnField.subschema || {}).map((subName) => {
                const subField = columnField.subschema?.[subName];
                if (!subField) return null;

                // Колонка не добавляет ключей в путь — только разметка, не поле
                const parentPath = fieldName.split(".").slice(0, -1).join(".");
                const fullFieldName = parentPath
                  ? `${parentPath}.${subName}`
                  : subName;

                return (
                  <React.Fragment key={subName}>
                    {this.getField(fullFieldName, subField, values, allValues)}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      );
    }

    // Handle card field
    if (type === "card") {
      const cardField = field as CardFieldDefinition;

      return (
        <Block
          key={fieldName}
          title={resolvedLabel || fieldName}
          hideHeader={cardField.hideHeader}
        >
          <Grid container spacing={this.props.spacing || 2}>
            {Object.keys(cardField.subschema || {}).map((subName) => {
              const subField = cardField.subschema?.[subName];
              if (!subField) return null;

              // Для карточек не добавляем имя карточки в путь к полю - карточка только для группировки UI
              // Используем родительский путь напрямую
              const parentPath = fieldName.split(".").slice(0, -1).join(".");
              const fullFieldName = parentPath
                ? `${parentPath}.${subName}`
                : subName;

              return (
                <React.Fragment key={subName}>
                  {this.getField(fullFieldName, subField, values, allValues)}
                </React.Fragment>
              );
            })}
          </Grid>
        </Block>
      );
    }

    // Handle array field specifically
    if (type === "array") {
      const arrayField = field as ArrayFieldDefinition;

      return (
        <ArrayField
          key={fieldName}
          fieldName={fieldName}
          field={arrayField}
          values={values}
          allValues={allValues}
          spacing={this.props.spacing}
          getField={this.getField}
          editData={this.props.editData}
        />
      );
    }

    // Handle platformBlocks field
    if (type === "platformBlocks") {
      const platformBlocksField = field as PlatformBlocksFieldDefinition;
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <PlatformBlocks
            name={fieldName}
            label={resolvedLabel || fieldName}
            alwaysOpen={platformBlocksField.alwaysOpen}
            values={values}
            platformId={allValues?.platformId as string | undefined}
            queries={platformBlocksField.queries}
          />
        </Grid>
      );
    }

    // Handle customFields (dynamic fields from query + getSchema)
    if (type === "customFields") {
      const customFieldsField = field as CustomFieldsFieldDefinition;
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <CustomFields
            name={fieldName}
            label={resolvedLabel ?? undefined}
            query={customFieldsField.query}
            queryVariables={customFieldsField.queryVariables}
            getSchema={customFieldsField.getSchema}
            allValues={allValues as Record<string, any>}
            disabled={disabled}
            fullWidth={restProps.fullWidth}
            spacing={this.props.spacing}
            xs={xs}
            sm={sm}
            md={md}
            lg={lg}
            xl={xl}
          />
        </Grid>
      );
    }

    // Handle customFieldsSchema (editing schema of custom fields — list + dialog)
    if (type === "customFieldsSchema") {
      const schemaField = field as CustomFieldsSchemaFieldDefinition;
      return (
        <CustomFieldsSchemaField
          key={fieldName}
          name={fieldName}
          label={resolvedLabel ?? undefined}
          helperText={resolvedHelperText ?? undefined}
          addText={schemaField.addText}
          typeOptions={schemaField.typeOptions}
          disabled={disabled}
          xs={xs}
          sm={sm}
          md={md}
          lg={lg}
          xl={xl}
        />
      );
    }

    // Handle fields with subschema (complex array fields)
    const fieldAsAny = field as any;
    if (fieldAsAny.subschema) {
      const getArrayFieldComponent = () => {
        switch (fieldAsAny.subType) {
          case "arenaBlocks":
          case "arenaTvBlocks":
          case "arenaOnboardingStep":
          case "block":
            return Block;
          default:
            return null;
        }
      };

      const ArrayFieldComponent = getArrayFieldComponent();

      if (ArrayFieldComponent) {
        return (
          <Grid
            key={fieldName}
            size={{
              xs: xs,
              sm: sm,
              md: md,
              lg: lg,
              xl: xl,
            }}
          >
            <FieldArray name={fieldName}>
              {() => (
                <ArrayFieldComponent
                  title={resolvedLabel || fieldName}
                  {...restProps}
                >
                  Array component placeholder
                </ArrayFieldComponent>
              )}
            </FieldArray>
          </Grid>
        );
      }

      // For other subschema fields, we'll use a simple approach
      return (
        <Grid
          key={fieldName}
          size={{
            xs: xs,
            sm: sm,
            md: md,
            lg: lg,
            xl: xl,
          }}
        >
          <FieldArray name={fieldName}>
            {({ fields }) => {
              const fieldArrayLabel =
                typeof label === "function"
                  ? label({ values, allValues })
                  : label;

              return (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <Typography variant="h6" component="h2">
                      {fieldArrayLabel}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const newItem = fieldAsAny.getNewItem
                          ? fieldAsAny.getNewItem()
                          : {};
                        fields.push(newItem);
                      }}
                      startIcon={<AddIcon />}
                    >
                      {fieldAsAny.addText}
                    </Button>
                  </div>

                  <div
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 4,
                      background: "transparent",
                      width: "100%",
                    }}
                  >
                    {fields.map((name: string, index: number) => {
                      const item = fields.value[index];

                      return (
                        <div
                          key={`${name}-${index}`}
                          style={{
                            display: "flex",
                            userSelect: "none" as const,
                            borderBottom:
                              index === (fields.length || 0) - 1
                                ? "none"
                                : "1px solid rgba(0, 0, 0, 0.12)",
                            padding: "16px 8px",
                          }}
                        >
                          <div style={{ width: "100%" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  cursor: "grab",
                                  height: "54px",
                                  alignSelf: "flex-start",
                                }}
                              >
                                <Typography variant="subtitle2">
                                  #{index + 1}
                                </Typography>
                              </div>
                              <div>
                                {fieldAsAny.softDelete && item?.deleted && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      fields.update(index, {
                                        ...item,
                                        deleted: false,
                                      });
                                    }}
                                    title={fieldAsAny.restoreText}
                                  >
                                    <SettingsBackupRestoreIcon />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (fieldAsAny.softDelete) {
                                      fields.update(index, {
                                        ...item,
                                        deleted: true,
                                      });
                                    } else {
                                      fields.remove(index);
                                    }
                                  }}
                                  title={fieldAsAny.removeText}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </div>
                            </div>
                            <Grid
                              container
                              spacing={this.props.spacing || 2}
                              style={{
                                opacity:
                                  fieldAsAny.softDelete && item?.deleted
                                    ? 0.5
                                    : 1,
                              }}
                            >
                              {Object.keys(fieldAsAny.subschema).map(
                                (subFieldName) => (
                                  <React.Fragment
                                    key={`${fieldName}.${subFieldName}`}
                                  >
                                    {this.getField(
                                      `${fieldName}.${subFieldName}`,
                                      {
                                        ...fieldAsAny.subschema[subFieldName],
                                        name: `${fieldName}.${subFieldName}`,
                                      },
                                      values,
                                      allValues
                                    )}
                                  </React.Fragment>
                                )
                              )}
                            </Grid>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }}
          </FieldArray>
        </Grid>
      );
    }

    const FieldComponent =
      fieldAsAny.component || this.getFieldComponent(type || "text");

    // Обрабатываем функции в пропсах
    const processedProps: any = { ...restProps };

    // Для fieldComponent с кастомным компонентом передаем editData, allValues и metadata
    if (
      type === "fieldComponent" &&
      (fieldAsAny.component || fieldAsAny.fieldComponent)
    ) {
      processedProps.editData = this.props.editData;
      processedProps.values = values;
      processedProps.allValues = allValues;
      processedProps.metadata = this.props.metadata;
    }

    // Для selectObject передаем allValues и editData
    if (type === "selectObject") {
      processedProps.allValues = allValues;
      processedProps.editData = this.props.editData;
    }

    // Обрабатываем helperText как функцию если это необходимо
    if (typeof field.helperText === "function") {
      const fieldValue = values[fieldName];
      processedProps.helperText = field.helperText({
        value: fieldValue,
        allValues,
      });
    }

    // Обрабатываем options как функцию если это необходимо (select/autocomplete)
    if (typeof processedProps.options === "function") {
      processedProps.options = processedProps.options({
        values,
        allValues,
        editData: this.props.editData,
      });
    }

    // Обрабатываем queryVariables как функцию если это необходимо
    if (typeof processedProps.queryVariables === "function") {
      processedProps.queryVariables = processedProps.queryVariables({
        values,
        allValues,
        editData: this.props.editData,
      });
    }

    // Обрабатываем createInitialVariables как функцию если это необходимо
    if (typeof processedProps.createInitialVariables === "function") {
      processedProps.createInitialVariables =
        processedProps.createInitialVariables({
          values,
          allValues,
          editData: this.props.editData,
        });
    }

    // Обрабатываем create.initialVariables как функцию если это необходимо
    if (
      processedProps.create?.initialVariables &&
      typeof processedProps.create.initialVariables === "function"
    ) {
      processedProps.create = {
        ...processedProps.create,
        initialVariables: processedProps.create.initialVariables({
          values,
          allValues,
          editData: this.props.editData,
        }),
      };
    }

    // Обрабатываем startAdornment как функцию если это необходимо
    if (typeof processedProps.startAdornment === "function") {
      processedProps.startAdornment = processedProps.startAdornment({
        editData: this.props.editData,
        allValues,
      });
    }

    // Обрабатываем endAdornment как функцию если это необходимо
    if (typeof processedProps.endAdornment === "function") {
      processedProps.endAdornment = processedProps.endAdornment({
        editData: this.props.editData,
        allValues,
      });
    }

    // Обрабатываем aiSuggestion как функцию если это необходимо
    if (typeof processedProps.aiSuggestion === "function") {
      processedProps.aiSuggestion = processedProps.aiSuggestion({
        editData: this.props.editData,
        allValues,
      });
    }

    // Обрабатываем createInitialVariables как функцию если это необходимо
    if (typeof processedProps.createInitialVariables === "function") {
      processedProps.createInitialVariables =
        processedProps.createInitialVariables({
          values,
          allValues,
          editData: this.props.editData,
        });
    }

    // Обрабатываем create.initialVariables как функцию если это необходимо
    if (
      processedProps.create?.initialVariables &&
      typeof processedProps.create.initialVariables === "function"
    ) {
      processedProps.create = {
        ...processedProps.create,
        initialVariables: processedProps.create.initialVariables({
          values,
          allValues,
          editData: this.props.editData,
        }),
      };
    }

    // Обрабатываем minDate как функцию если это необходимо
    if ("minDate" in field && typeof field.minDate === "function") {
      processedProps.minDate = field.minDate({
        values,
        allValues,
      });
    }

    // Обрабатываем maxDate как функцию если это необходимо
    if ("maxDate" in field && typeof field.maxDate === "function") {
      processedProps.maxDate = field.maxDate({
        values,
        allValues,
      });
    }

    return (
      <Grid
        key={fieldName}
        size={{
          xs: xs,
          sm: sm,
          md: md,
          lg: lg,
          xl: xl,
        }}
      >
        <FieldComponent
          name={fieldName}
          label={label}
          disabled={disabled}
          type={type}
          metadata={this.props.metadata}
          {...processedProps}
        />
      </Grid>
    );
  };

  onSubmit = async (values: Record<string, any>) => {
    const result = this.props.onSubmit?.(values);

    if (!result) return result;
    if (!result.then) return result;

    return result
      .then((result: any) => {
        // После успешного сохранения pristine автоматически станет true
        // поэтому нам не нужно ничего делать вручную
        this.props.onSubmitSuccess?.(result);
        return result;
      })
      .catch((error: any) => {
        this.props.onSubmitFailure?.(error);
        return { [FORM_ERROR]: error.message };
      });
  };

  onChangeTab = (_: React.SyntheticEvent, value: number) => {
    this.setState({
      activeTab: value,
    });
  };

  renderTabs(): ReactNode {
    const { activeTab } = this.state;
    const { schema } = this.props;

    if (!schema.tabs) return null;

    return (
      <AppBar position="static">
        <Tabs value={activeTab} onChange={this.onChangeTab}>
          {Object.keys(schema.tabs).map((tabName, index) => {
            const tab = schema.tabs?.[tabName];
            return <Tab value={index} key={index} label={tab?.label} />;
          })}
        </Tabs>
      </AppBar>
    );
  }

  renderTabsContent(
    values: Record<string, any> = {},
    allValues: Record<string, any> = {}
  ): ReactNode {
    const { activeTab } = this.state;
    const { schema, spacing } = this.props;

    if (!schema.tabs) return null;

    return Object.keys(schema.tabs).map((tabName, index) => {
      const tabSchema = schema.tabs?.[tabName]?.schema || {};
      return (
        <Grid
          key={index}
          style={{ display: activeTab === index ? undefined : "none" }}
          size={12}
        >
          <Grid container spacing={spacing}>
            {Object.keys(tabSchema).map((name) => (
              <React.Fragment key={name}>
                {this.getField(name, tabSchema[name], values, allValues)}
              </React.Fragment>
            ))}
          </Grid>
        </Grid>
      );
    });
  }

  render(): ReactNode {
    const {
      schema,
      spacing,
      disabled,
      submitButton,
      resetButton,
      submitButtonText: submitButtonTextProp,
      resetButtonText: resetButtonTextProp,
      actionsAlign,
      header,
      formId,
      indent,
      validate,
      ...props
    } = this.props;

    const { confirm, loadingConfirm } = this.state;

    const submitButtonText =
      submitButtonTextProp ?? tShared("shared.form.submit", "Сохранить");
    const resetButtonText =
      resetButtonTextProp ?? tShared("shared.form.reset", "Отмена");

    return (
      <>
        <Form
          {...props}
          subscription={{
            submitting: true,
            pristine: true,
            values: true,
          }}
          mutators={{ ...arrayMutators }}
          validate={validate}
          onSubmit={this.onSubmit}
          render={({
            handleSubmit,
            submitting,
            pristine,
            form,
          }: FormRenderProps<Record<string, any>>) => {
            // Сохраняем текущее состояние pristine для использования в обработчиках предупреждений
            this.currentPristine = pristine ?? true;

            const { submitError, errors, values } = form.getState();
            const allValues =
              values && Object.keys(values).length
                ? values
                : this.props.initialValues || {};

            let tabsHead: ReactNode = null;
            let fields: ReactNode = null;

            if (schema.tabs) {
              tabsHead = this.renderTabs();
              fields = this.renderTabsContent(values || {}, allValues);
            } else {
              const filteredFields = Object.keys(schema).filter((name) => {
                const fieldDef = schema[name] as FieldDefinition;
                return fieldDef?.isVisible
                  ? fieldDef.isVisible({ values: values || {}, allValues })
                  : true;
              });

              fields = filteredFields.map((name) => (
                <React.Fragment key={name}>
                  {this.getField(
                    name,
                    schema[name] as FieldDefinition,
                    values || {},
                    allValues
                  )}
                </React.Fragment>
              ));
            }

            const onSubmitWithConfirm = (event: React.FormEvent) => {
              const { values } = form.getState();
              const confirmData =
                this.props.getConfirm &&
                this.props.getConfirm({ values: values || {} });

              if (confirmData) {
                event.preventDefault();
                event.stopPropagation();
                this.setState({
                  confirm: confirmData,
                });
              } else {
                return handleSubmit(event);
              }
            };

            const onConfirm = () => {
              this.setState({
                confirm: null,
              });

              handleSubmit();
            };

            const onCancelConfirm = () => {
              this.setState({
                confirm: null,
              });
            };

            const WrapperComponent = this.props.paper ? Paper : Fragment;

            return (
              <>
                <form
                  id={formId}
                  onSubmit={onSubmitWithConfirm}
                  onReset={() => form.reset()}
                  autoComplete="off"
                  name="FormBuilder"
                >
                  {this.props.onChange || this.props.warnOnUnsavedChanges ? (
                    <FormSpy
                      subscription={{ values: true, pristine: true }}
                      onChange={(formState) => {
                        if (
                          !this.prevValues ||
                          this.prevValues !== formState.values
                        ) {
                          this.prevValues = formState.values;

                          // Вызываем onChange только если форма не pristine (пользователь что-то изменил)
                          // Это предотвращает бесконечный цикл при инициализации
                          if (
                            this.props.onChange &&
                            !formState.pristine &&
                            formState.values
                          ) {
                            this.props.onChange({ values: formState.values });
                          }

                          // Теперь используем pristine из React Final Form,
                          // поэтому checkForUnsavedChanges больше не нужен
                        }
                      }}
                    />
                  ) : null}

                  {header &&
                    header({
                      disabled: Boolean(submitting || pristine || disabled),
                    })}

                  {tabsHead}
                  <div style={{ padding: indent ? 8 : 0 }}>
                    <WrapperComponent sx={{ p: this.props.paper ? 2 : 0 }}>
                      <Grid container spacing={spacing}>
                        {(submitError || errors?.global) && (
                          <Grid size={12}>
                            <ErrorMessage
                              error={{
                                message: submitError || errors?.global,
                              }}
                            />
                          </Grid>
                        )}
                        {fields}
                        {(submitButton || resetButton) &&
                          actionsAlign === "end" && (
                            <Grid size={12}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                {resetButton && (
                                  <Button
                                    variant="outlined"
                                    type="reset"
                                    disabled={
                                      submitting || pristine || disabled
                                    }
                                  >
                                    {resetButtonText}
                                  </Button>
                                )}
                                {submitButton && (
                                  <Button
                                    disableElevation
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={
                                      submitting || pristine || disabled
                                    }
                                  >
                                    {submitButtonText}
                                  </Button>
                                )}
                              </Stack>
                            </Grid>
                          )}
                        {(submitButton || resetButton) &&
                          actionsAlign !== "end" && (
                            <Grid size={12}>
                              <Grid container spacing={spacing}>
                                {submitButton && (
                                  <Grid size={resetButton ? 6 : 12}>
                                    <Button
                                      fullWidth
                                      disableElevation
                                      variant="contained"
                                      color="primary"
                                      type="submit"
                                      size="large"
                                      disabled={
                                        submitting || pristine || disabled
                                      }
                                    >
                                      {submitButtonText}
                                    </Button>
                                  </Grid>
                                )}
                                {resetButton && (
                                  <Grid size={6}>
                                    <Button
                                      fullWidth
                                      variant="outlined"
                                      type="reset"
                                      size="large"
                                      disabled={
                                        submitting || pristine || disabled
                                      }
                                    >
                                      {resetButtonText}
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          )}
                      </Grid>
                    </WrapperComponent>
                  </div>
                </form>
                {confirm && (
                  <ConfirmDialog
                    open={!!confirm}
                    onCancel={onCancelConfirm}
                    onConfirm={onConfirm}
                    confirmTitle={confirm.confirmTitle}
                    confirmText={confirm.confirmText}
                    delay={confirm.delay}
                    loading={loadingConfirm}
                  />
                )}
              </>
            );
          }}
        />
      </>
    );
  }
}
